'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import { ReturnOnInvestmentCardPropTypes } from './ReturnOnInvestmentCard.types';

const ReturnOnInvestmentCard: React.FC<ReturnOnInvestmentCardPropTypes> = ({}) => {
  const isMobile = useIsMobile();

  // --- Input state ---
  const [sessionPrice, setSessionPrice] = React.useState('');
  const [monthlyInvestment, setMonthlyInvestment] = React.useState('');
  const [tempSessionPrice, setTempSessionPrice] = React.useState('');
  const [tempMonthlyInvestment, setTempMonthlyInvestment] = React.useState('');

  // --- Currency unit state ---
  const [unit, setUnit] = React.useState('usd');

  // Apply mobile changes
  const handleCalculate = () => {
    setSessionPrice(tempSessionPrice);
    setMonthlyInvestment(tempMonthlyInvestment);
  };

  // On mobile, detect edits that haven’t been confirmed yet
  const hasPendingChanges =
    isMobile && (tempSessionPrice !== sessionPrice || tempMonthlyInvestment !== monthlyInvestment);

  // Stubbed 1:1 conversion
  const convertToUSD = (value: string) => {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  };

  // Perform calculation: sessionsRequired = monthlyInvestment / sessionPrice
  const sessionPriceUSD = convertToUSD(sessionPrice);
  const monthlyInvestmentUSD = convertToUSD(monthlyInvestment);
  let sessionsRequired = 0;
  if (sessionPriceUSD > 0) {
    sessionsRequired = monthlyInvestmentUSD / sessionPriceUSD;
  }

  const formattedSessions = sessionsRequired.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the “formattedSessions” so it only updates 3 seconds after typing stops ---
  const debouncedSessions = useDebounce(formattedSessions, 3000);

  // Fire a tracking event whenever the debounced value settles
  React.useEffect(() => {
    // Only fire if both inputs are non-empty and the number is valid
    if (
      sessionPrice !== '' &&
      monthlyInvestment !== '' &&
      !isNaN(Number(debouncedSessions)) &&
      isFinite(Number(debouncedSessions))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_return_on_investment',
          value: 1,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSessions]);

  return (
    <CardMetric
      id='calculator-return-on-investment'
      title='ROI'
      productIcons={[
        {
          alt: 'HumanTrak App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/XjLSuUERWGGH5l8YUIGK5/4d79255d0480499ea0c5684e2ea2f1fc/humantrack-app-icon.svg',
        },
        {
          alt: 'SmartSpeed App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/Dg2ZJXDIhN88rg19CH7JT/53585f72a882a38c9b9862986b7327e3/smartspeed-app-icon.svg',
        },
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'NordBord App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/6iS357n4siVSm6nFZeci6q/544cd567518357e813ac48f15dae44f4/nordbord-app-icon.svg',
        },
        {
          alt: 'Dynamo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        Enter your business’ numbers and desired investment value to calculate your potential{' '}
        <strong className='font-medium'>Return on Investment.</strong>
      </p>

      {/* Show the fraction: monthlyInvestment ÷ sessionPrice */}
      <FractionDisplay
        className='mb-[2rem]'
        label='Return on Investment'
        numerator='<strong className="font-medium block sm:inline-block">Investment Price (Monthly)</strong>'
        denominator='<strong className="font-medium block sm:inline-block">Single Session Price</strong>'
      />

      {/* Inputs */}
      <div>
        <ForceInput
          className='mb-[1.4rem]'
          placeholder='USD'
          labelLines={['Single Session Price:', 'Gross Profits']}
          units={[
            { name: 'USD', unit: '$', id: 'usd' },
            { name: 'AUD', unit: '$', id: 'aud' },
            { name: 'EUR', unit: '€', id: 'eur' },
          ]}
          activeUnit={unit}
          value={isMobile ? tempSessionPrice : sessionPrice}
          onUnitSelect={(u) => setUnit(u)}
          onChange={(e) => {
            isMobile ? setTempSessionPrice(e.target.value) : setSessionPrice(e.target.value);
          }}
        />

        <ForceInput
          className='mb-[1.4rem]'
          placeholder='USD'
          labelLines={['Investment Price (Monthly):', 'Gross Losses']}
          units={[
            { name: 'USD', unit: '$', id: 'usd' },
            { name: 'AUD', unit: '$', id: 'aud' },
            { name: 'EUR', unit: '€', id: 'eur' },
          ]}
          activeUnit={unit}
          value={isMobile ? tempMonthlyInvestment : monthlyInvestment}
          onUnitSelect={(u) => setUnit(u)}
          onChange={(e) => {
            isMobile
              ? setTempMonthlyInvestment(e.target.value)
              : setMonthlyInvestment(e.target.value);
          }}
        />
      </div>

      {/* Result display */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>
          Monthly Sessions Required to Realize ROI
        </span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {hasPendingChanges ? '0.0' : !isFinite(sessionsRequired) ? '0.0' : formattedSessions}
        </span>
      </div>

      {/* Help link & mobile calculate button */}
      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            <NextLink
              className='font-light underline'
              href='https://resources.valdhealth.com/practitioners-guide-to-roi'
            >
              Click here
            </NextLink>{' '}
            to learn more about ROI from the VALD Practitioner’s Guide to ROI.
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            disabled={!hasPendingChanges}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default ReturnOnInvestmentCard;
